import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Source, Layer } from "react-map-gl/mapbox";
import * as turf from "@turf/turf";

import { formatDistanceString } from "../helpers";

export const FLOOD_COAST_MARKER_LAYER_ID = "flood-coast-marker-layer";
export const FLOOD_STREAM_MARKER_LAYER_ID = "flood-stream-marker-layer";
export const FLOOD_COAST_LINE_LAYER_ID = "flood-coast-line-layer";
export const FLOOD_STREAM_LINE_LAYER_ID = "flood-stream-line-layer";
export const FLOOD_COAST_LABEL_LAYER_ID = "flood-coast-label-layer";
export const FLOOD_STREAM_LABEL_LAYER_ID = "flood-stream-label-layer";

const FLAVOURS = {
  COASTAL: "coastal",
  FLUVIAL: "fluvial",
  FLUVIAL_COASTAL: "fluvial_coastal",
};

const FloodDistanceMarkers = ({ markerInfo }) => {
  const { t } = useTranslation();
  const floodData = useMemo(() => {
    if (!markerInfo?.riskData) return null;

    const dataSource = markerInfo.riskData.flood;
    if (!dataSource) return null;

    const flavour =
      dataSource.fluvial_coastal === "costal"
        ? FLAVOURS.COASTAL
        : dataSource.fluvial_coastal;

    if (
      flavour !== FLAVOURS.COASTAL &&
      flavour !== FLAVOURS.FLUVIAL &&
      flavour !== FLAVOURS.FLUVIAL_COASTAL
    ) {
      return null;
    }

    const showCoast =
      flavour === FLAVOURS.COASTAL || flavour === FLAVOURS.FLUVIAL_COASTAL;
    const showStream =
      flavour === FLAVOURS.FLUVIAL || flavour === FLAVOURS.FLUVIAL_COASTAL;

    if (showCoast) {
      if (
        dataSource.dist_to_coast === undefined ||
        dataSource.nearest_coast_point_x == null ||
        dataSource.nearest_coast_point_y == null
      ) {
        return null;
      }
    }
    if (showStream) {
      if (
        dataSource.dist_to_stream === undefined ||
        dataSource.nearest_stream_point_x == null ||
        dataSource.nearest_stream_point_y == null
      ) {
        return null;
      }
    }

    return {
      mainPoint: [markerInfo.riskData.longitude, markerInfo.riskData.latitude],
      coastPoint: [
        dataSource.nearest_coast_point_x,
        dataSource.nearest_coast_point_y,
      ],
      streamPoint: [
        dataSource.nearest_stream_point_x,
        dataSource.nearest_stream_point_y,
      ],
      distToCoast: dataSource.dist_to_coast,
      distToStream: dataSource.dist_to_stream,
      showCoast,
      showStream,
    };
  }, [markerInfo]);

  const coastMarkerData = useMemo(() => {
    if (!floodData?.showCoast) return null;
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: floodData.coastPoint,
      },
      properties: {
        distance: floodData.distToCoast,
      },
    };
  }, [floodData]);

  const streamMarkerData = useMemo(() => {
    if (!floodData?.showStream) return null;
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: floodData.streamPoint,
      },
      properties: {
        distance: floodData.distToStream,
      },
    };
  }, [floodData]);

  const coastLineData = useMemo(() => {
    if (!floodData?.showCoast) return null;
    return turf.lineString([floodData.mainPoint, floodData.coastPoint], {
      distance: floodData.distToCoast,
    });
  }, [floodData]);

  const streamLineData = useMemo(() => {
    if (!floodData?.showStream) return null;
    return turf.lineString([floodData.mainPoint, floodData.streamPoint], {
      distance: floodData.distToStream,
    });
  }, [floodData]);

  const coastLabelData = useMemo(() => {
    if (!floodData?.showCoast) return null;
    const midpoint = turf.midpoint(
      turf.point(floodData.mainPoint),
      turf.point(floodData.coastPoint),
    );
    return {
      type: "FeatureCollection",
      features: [
        {
          ...midpoint,
          properties: {
            distance: formatDistanceString(floodData.distToCoast, t),
          },
        },
      ],
    };
  }, [floodData, t]);

  const streamLabelData = useMemo(() => {
    if (!floodData?.showStream) return null;
    const midpoint = turf.midpoint(
      turf.point(floodData.mainPoint),
      turf.point(floodData.streamPoint),
    );
    return {
      type: "FeatureCollection",
      features: [
        {
          ...midpoint,
          properties: {
            distance: formatDistanceString(floodData.distToStream, t),
          },
        },
      ],
    };
  }, [floodData, t]);

  if (!floodData) return null;

  return (
    <>
      {/* Coast Marker */}
      {coastMarkerData && (
        <Source
          id="flood-coast-marker-source"
          type="geojson"
          data={coastMarkerData}
        >
          <Layer
            id={FLOOD_COAST_MARKER_LAYER_ID}
            type="circle"
            paint={{
              "circle-radius": 6,
              "circle-color": "#FFB703",
              "circle-opacity": 1,
              "circle-stroke-width": 2,
              "circle-stroke-color": "white",
              "circle-pitch-alignment": "map",
            }}
          />
        </Source>
      )}

      {/* Stream Marker */}
      {streamMarkerData && (
        <Source
          id="flood-stream-marker-source"
          type="geojson"
          data={streamMarkerData}
        >
          <Layer
            id={FLOOD_STREAM_MARKER_LAYER_ID}
            type="circle"
            paint={{
              "circle-radius": 6,
              "circle-color": "#FFB703",
              "circle-opacity": 1,
              "circle-stroke-width": 2,
              "circle-stroke-color": "white",
              "circle-pitch-alignment": "map",
            }}
          />
        </Source>
      )}

      {/* Coast Line */}
      {coastLineData && (
        <Source
          id="flood-coast-line-source"
          type="geojson"
          data={coastLineData}
        >
          <Layer
            id={FLOOD_COAST_LINE_LAYER_ID}
            type="line"
            paint={{
              "line-color": "#FFB703",
              "line-width": 4,
              "line-opacity": 0.8,
              "line-occlusion-opacity": 1,
            }}
          />
        </Source>
      )}

      {/* Stream Line */}
      {streamLineData && (
        <Source
          id="flood-stream-line-source"
          type="geojson"
          data={streamLineData}
        >
          <Layer
            id={FLOOD_STREAM_LINE_LAYER_ID}
            type="line"
            paint={{
              "line-color": "#FFB703",
              "line-width": 4,
              "line-opacity": 0.8,
              "line-occlusion-opacity": 1,
            }}
          />
        </Source>
      )}

      {/* Coast Distance Label */}
      {coastLabelData && (
        <Source
          id="flood-coast-label-source"
          type="geojson"
          data={coastLabelData}
        >
          <Layer
            id={FLOOD_COAST_LABEL_LAYER_ID}
            type="symbol"
            layout={{
              "text-field": ["get", "distance"],
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
              "text-size": 15,
              "text-anchor": "center",
              "text-allow-overlap": true,
            }}
            paint={{
              "text-color": "#000",
              "text-halo-color": "white",
              "text-halo-width": 1.5,
              "text-occlusion-opacity": 1,
            }}
          />
        </Source>
      )}

      {/* Stream Distance Label */}
      {streamLabelData && (
        <Source
          id="flood-stream-label-source"
          type="geojson"
          data={streamLabelData}
        >
          <Layer
            id={FLOOD_STREAM_LABEL_LAYER_ID}
            type="symbol"
            layout={{
              "text-field": ["get", "distance"],
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
              "text-size": 15,
              "text-anchor": "center",
              "text-allow-overlap": true,
            }}
            paint={{
              "text-color": "#000",
              "text-halo-color": "white",
              "text-halo-width": 1.5,
              "text-occlusion-opacity": 1,
            }}
          />
        </Source>
      )}
    </>
  );
};

export default FloodDistanceMarkers;
